// pages/forgot-password.jsx

import { hubThemeLight } from '@/ui/theme';
import { CustomThemeProvider } from '@/ui/theme/ThemeContext';
import { switchAuthFlowType } from '@/utils/aws/cognito/config';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, Paper, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { requestPasswordReset } from '@/utils/aws/cognito/amplifyAuth';
import axios from 'axios';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { toast } from 'react-toastify';
import ErrorBoundary from '../../components/Reusable/ErrorHandling/ErrorBoundary';
import classes from '../../styles/login.module.css';

const ForgotPassword = () => {
  const router = useRouter();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '' });

  function sendForgotPasswordEmail(err, email) {
    if (err.code != null && err.code === 'NotAuthorizedException') {
      const newAxios = axios.create();
      newAxios({
        method: 'post',
        url: process.env.NEXT_PUBLIC_INVOKE_URL + '/forgotPassword',
        data: { email: email },
      }).catch((err) => {
        const message = err?.response?.data?.message;
        console.error(err);
        setLoading(false);
        toast.error(message);
        return;
      });
      toast.success('Password successfully sent to your email.');
      router.push('/login');
    } else if (err.code != null && err.code === 'UserNotFoundException') {
      setLoading(false);
      toast.error('User Not Found');
    } else if (
      err != null &&
      typeof err === 'string' &&
      err.includes('LimitExceededException')
    ) {
      setLoading(false);
      toast.error(
        'Too many password reset attempts. Please wait a few minutes before trying again.'
      );
    } else {
      setLoading(false);
      toast.error(err.message || 'An error occurred. Please try again.');
    }
  }

  function goToReset(data, email) {
    setLoading(false);
    router.push({
      pathname: '/confirm-password-reset',
      query: { email: email },
    });
  }

  const submitForm = async () => {
    setLoading(true);
    // Switch to USER_SRP_AUTH for forgotPassword
    switchAuthFlowType('USER_SRP_AUTH');
    try {
      const trimEmail = formData.email.trim();
      await requestPasswordReset(trimEmail)
        .then((data) => goToReset(data, trimEmail))
        .catch((err) => sendForgotPasswordEmail(err, trimEmail));
    } catch (error) {
      setLoading(false);
      toast.error(`Failed to reset password. ${error.message}`);
    } finally {
      // Always switch back to CUSTOM_AUTH for future logins
      switchAuthFlowType('CUSTOM_AUTH');
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <CustomThemeProvider initialSettings={{ themeOverride: hubThemeLight }}>
      <Head>
        <title>Forgot Password | Cornerstone Upstream</title>
      </Head>
      <Grid
        container
        spacing={0}
        direction='column'
        alignItems='center'
        className={classes.wrapper}
        style={{
          backgroundImage: `url("${process.env.NEXT_PUBLIC_STATIC_ASSETS_URL}cis-hub-bg.jpg")`,
        }}
      >
        <Box m='auto' width={'80%'} maxWidth={400}>
          <div className={classes.logo} />

          <Box
            component={Paper}
            width={'100%'}
            bgcolor='white'
            justifyContent='center'
            display='flex'
            elevation={12}
            className={classes.paper}
          >
            {/* ─── START FORM CONTENT ─── */}
            <Box
              component='form'
              onSubmit={(e) => {
                e.preventDefault();
                submitForm();
              }}
              sx={{
                px: { xs: 2, sm: 3, md: 4 },
                py: 3,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              {/* "Forgot Password?" Heading - h1 for page title and heading structure */}
              <Typography
                component='h1'
                variant='h5'
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 3, // 24px below heading
                  lineHeight: 1.2,
                }}
              >
                Forgot Password?
              </Typography>

              {/* Description Text */}
              <Typography
                sx={{
                  fontSize: 14,
                  color: theme.palette.text.primary,
                  mb: 4, // 32px below description
                  lineHeight: 1.5,
                }}
              >
                Enter the email associated with your account and we'll send an
                email with instructions to reset your password.
              </Typography>

              {/* Email Field */}
              <ErrorBoundary>
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Email'
                  name='email'
                  type='email'
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder='Enter your email address'
                  InputLabelProps={{
                    sx: { fontWeight: theme.typography.fontWeightMedium },
                  }}
                  sx={{
                    mb: 3, // 24px below TextField
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      background: theme.palette.background.default,
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: 14,
                    },
                  }}
                />
              </ErrorBoundary>

              {/* “Send Reset” Button */}
              <ErrorBoundary>
                <LoadingButton
                  fullWidth
                  loading={loading}
                  variant='contained'
                  type='submit'
                  disabled={!formData.email || loading}
                  sx={{
                    background: theme.palette.primary.main,
                    '&:hover': { background: theme.palette.primary.dark },
                    borderRadius: '8px',
                    py: 1.5, // 48px tall
                    fontWeight: 700,
                    fontSize: 18,
                    boxShadow: '0 2px 8px rgba(79,124,247,0.12)',
                    textTransform: 'none',
                    mb: 3, // 24px below button
                  }}
                >
                  Send Reset
                </LoadingButton>
              </ErrorBoundary>

              {/* “Return to login” Link */}
              <ErrorBoundary>
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Box
                    component='a'
                    href='/login'
                    role='link'
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: 14,
                      fontWeight: theme.typography.fontWeightSemiBold,
                      color: theme.palette.text.primary,
                      textDecoration: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <ChevronLeftIcon
                      sx={{ fontSize: 20, mr: 0.5, verticalAlign: 'middle' }}
                    />
                    Return to sign in
                  </Box>
                </Box>
              </ErrorBoundary>

              {/* “I never finished registering…” Link */}
              <ErrorBoundary>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    component={Link}
                    href='/finish-registration'
                    sx={{
                      fontSize: 14,
                      color: theme.palette.text.primary,
                      textDecoration: 'none',
                      fontWeight: 700,
                    }}
                  >
                    I never finished registering my user
                  </Typography>
                </Box>
              </ErrorBoundary>
            </Box>
            {/* ─── END FORM CONTENT ─── */}
          </Box>
        </Box>
      </Grid>
    </CustomThemeProvider>
  );
};

export default ForgotPassword;
